/* eslint-disable camelcase */
import { CircularProgress, makeStyles, Theme, Paper } from '@material-ui/core';
import { GenericMessageBox } from '@moderepo/mode-app-components';
import { EntityClass } from '@moderepo/mode-internal-apis';
import clsx from 'clsx';
import React, { CSSProperties, useCallback, useMemo, useRef } from 'react';
import { EntityUIComponentCollection, FetchEntityTimeSpanDataParams, FetchEntityTimeRangeDataParams, useSelectTranslatedEntityClasses,
    EntityUIComponentProps } from '.';
import { useRitzTranslation } from '../../utils/customHooks';
import { useSelectEntity, useSelectProject } from '../../utils/dataStateHooks';
import { RitzComponentsCollection } from '../../utils/ritzComponentsCollection';
import { SettingsErrorLogger } from '../../utils/logger';
import { EntityDetailsPageLayout, EntityDetailsUIComponentSettings, EntityDetailsCompsPlacement } from './EntityDetailsLayouts';



const useStyle = (layout: EntityDetailsPageLayout | undefined) => {
    return makeStyles((theme: Theme) => {

        // The 'templateRows', 'templateColumns', and 'templateAreas', set inside layout will be used as default for ALL screen, or at least
        // large screens
        const defaultTemplateRows = layout?.templateRows;
        const defaultTemplateColumns = layout?.templateColumns;
        const defaultTemplateAreas = layout?.templateAreas || [];

        // If layout contain settings for mediumScreen then use it on medium screen. If not, continue to use default for medium screens as well
        const mediumTemplateRows = layout?.mediumScreen?.templateRows || defaultTemplateRows;
        const mediumTemplateColumns = layout?.mediumScreen?.templateColumns || defaultTemplateColumns;
        const mediumTemplateAreas = layout?.mediumScreen?.templateAreas || defaultTemplateAreas || [];

        // If layout contain settings for smallScreen then use it on small screen. If not, continue to use default for medium/large screens
        const smallTemplateRows = layout?.smallScreen?.templateRows || mediumTemplateRows;
        const smallTemplateColumns = layout?.smallScreen?.templateColumns || mediumTemplateColumns;
        const smallTemplateAreas = layout?.smallScreen?.templateAreas || mediumTemplateAreas || [];

        return {
            root: {
                display            : 'grid',
                gridGap            : theme.spacing(1),
                gridTemplateRows   : smallTemplateRows,
                gridTemplateColumns: smallTemplateColumns,
                gridTemplateAreas  : `${smallTemplateAreas.map((row) => {
                    return `'${row.join(' ')}'`;
                }).join('\n')}`,
            },

            area: {
                '&:empty': {
                    display: 'none',
                },
            },

            componentsContainer: {
                '& .component': {
                    '&:not(:last-child)': {
                        paddingBottom: theme.spacing(1),
                    },
                    '&:empty': {
                        display: 'none',
                    },
                },
            },

            [theme.breakpoints.up('md')]: {
                root: {
                    gridGap            : theme.spacing(2),
                    gridTemplateRows   : mediumTemplateRows,
                    gridTemplateColumns: mediumTemplateColumns,
                    gridTemplateAreas  : `${mediumTemplateAreas.map((row) => {
                        return `'${row.join(' ')}'`;
                    }).join('\n')}`,
                },
                area: {
                },
                componentsContainer: {
                    '& .component': {
                        '&:not(:last-child)': {
                            paddingBottom: theme.spacing(2),
                        },
                    },
                },
            },

            [theme.breakpoints.up('lg')]: {
                root: {
                    gridGap            : theme.spacing(3),
                    gridTemplateRows   : defaultTemplateRows,
                    gridTemplateColumns: defaultTemplateColumns,
                    gridTemplateAreas  : `${defaultTemplateAreas.map((row) => {
                        return `'${row.join(' ')}'`;
                    }).join('\n')}`,
                },
                area: {
                },
                componentsContainer: {
                    '& .component': {
                        '&:not(:last-child)': {
                            paddingBottom: theme.spacing(3),
                        },
                    },
                },
            },
        };
    }, { name: 'EntityDetailsGenericLayoutContainer', index: 1 });
};



/**
 * A helper function to check if a layout's 'templateAreas' has configuration for a specific area.
 * Not all layouts has configuration for all areas. For example, some layout might have area for map and some don't and if a layout doesn't
 * have map, we will not display map. Therefore, before we create an area for an entity layout, we need to check if the area is needed for
 * that layout and so we need to call this helper function.
 */
export const doesLayoutHasArea = (layout: EntityDetailsPageLayout, area: string): boolean => {
    return Boolean(layout.templateAreas.find((row) => {
        return row.includes(area);
    }) !== undefined);
};



export interface EntityDetailsGenericLayoutContainerProps {
    readonly className?: string | undefined;
    readonly style?: CSSProperties | undefined;
    readonly projectId: number;
    readonly entityId: string;

    // The params for fetching data. Most of these params are from user inputs from the Entity control bar
    readonly fetchDataParams?: FetchEntityTimeSpanDataParams | FetchEntityTimeRangeDataParams | undefined;

    readonly pageLayout: EntityDetailsPageLayout;
    readonly componentsPlacement: EntityDetailsCompsPlacement;
    readonly onShowEntityDetails: EntityUIComponentProps['onShowEntityDetails'];
    readonly onShowEntityPreview: EntityUIComponentProps['onShowEntityPreview'];
}



/**
 * This component display info for 1 entity. For now it will only display the entity name and ID but this component will
 * be displaying entity data e.g charts, dashboard, etc...
 */
export const EntityDetailsGenericLayoutContainer: React.FC<EntityDetailsGenericLayoutContainerProps> = (
    props: EntityDetailsGenericLayoutContainerProps,
) => {

    const project = useSelectProject(props.projectId);
    const { trans } = useRitzTranslation(project);
    const entityClasses = useSelectTranslatedEntityClasses(props.projectId, trans);
    const selectedEntity = useSelectEntity(props.projectId, props.entityId, false);

    // Find the entityClass object that the selectedEntity belonged to
    const selectedEntityClass = useMemo(() => {
        if (selectedEntity && entityClasses) {
            return entityClasses.find((entityClass) => {
                return entityClass.entityClass === selectedEntity.entityClass;
            });
        }
        return undefined;
    }, [entityClasses, selectedEntity]);

    const classes = useStyle(props.pageLayout)();

    // To keep track of all the components' customSettings that we have already validated so that we don't keep doing it every time
    // We will use the component's customSettings object as the key to the map and a boolean to store the validated. "true" means the component
    // settings is validated successfully. "false" means the component's customSettings is validated and it is invalid. "undefined" means the
    // component is NOT validated.
    const componentCustomSettingsValidatedStatus = useRef(new Map<{}, boolean>());


    /**
     * Create
     */
    const createEntityUIComponent = useCallback((comp: string | EntityDetailsUIComponentSettings, entityClass: EntityClass) => {
        const key = JSON.stringify(comp);

        const compType = typeof comp === 'object' ? comp.componentType : comp;
        const componentInfo = EntityUIComponentCollection.getInstance().getComponent(compType);

        if (!componentInfo) {
            return (
                <div className="component" key={key}>
                    {`Unknown Component Type '${compType}'`}
                </div>
            );
        }

        const Component = componentInfo.componentFunc;
        const customSettingsValidator = componentInfo.customSettingsValidator;

        // Get the comp's customSettings if it is a type of EntityDetailsComponentSettings
        const customSettings = (() => {
            const customSettingsObject = typeof comp === 'object' ? comp.customSettings : undefined;

            // If there is a customSettings object AND if there is a validator function to validate the customSettings object,
            // call the validator function to validate the object
            if (customSettingsObject) {

                // Check the cache the see if the component's customSettings has been validated
                const isValid = componentCustomSettingsValidatedStatus.current.get(customSettingsObject);

                if (isValid === false
                    || (isValid === undefined && customSettingsValidator && !customSettingsValidator(
                        customSettingsObject,
                        new SettingsErrorLogger(`component "${compType}" in entity class "${entityClass.entityClass}"`, comp),
                        ['customSettings'],
                    ))
                ) {
                    // component's customSettings is INVALID, remember that it has been validated and failed
                    componentCustomSettingsValidatedStatus.current.set(customSettingsObject, false);
                    return new Error('Invalid customSettings');
                }

                // component's customSettings is VALID, remember that it has been validated and it was success
                componentCustomSettingsValidatedStatus.current.set(customSettingsObject, true);

                return customSettingsObject;
            }

            return undefined;
        })();

        if (customSettings instanceof Error) {
            return (
                <div className="component" key={key}>
                    <Paper elevation={2}>
                        <GenericMessageBox
                            key={key}
                            title="Invalid Config For"
                            message={compType}
                        />
                    </Paper>
                </div>
            );
        }

        return (
            <div className="component" key={key}>
                <Component
                    projectId={props.projectId}
                    entityId={props.entityId}
                    customSettings={customSettings}
                    fetchDataParams={props.fetchDataParams}
                    onShowEntityDetails={props.onShowEntityDetails}
                    onShowEntityPreview={props.onShowEntityPreview}
                />
            </div>
        );
    }, [props.entityId, props.fetchDataParams, props.onShowEntityDetails, props.onShowEntityPreview, props.projectId]);



    return (
        <>
            {project && selectedEntity && selectedEntityClass ? (
                <div className={classes.root}>
                    {Object.keys(props.componentsPlacement).map((area) => {
                        if (doesLayoutHasArea(props.pageLayout, area)) {
                            const components = props.componentsPlacement[area]?.filter((component) => {
                                // Filter out components that are marked as 'hidden'
                                if (typeof component === 'object' && component.hidden === true) {
                                    return false;
                                }
                                return true;
                            });
                            return (
                                <div
                                    key={area}
                                    className={clsx(classes.area, classes.componentsContainer)}
                                    style={{ gridArea: area }}
                                >
                                    {components?.map((comp) => {
                                        return createEntityUIComponent(comp, selectedEntityClass);
                                    })}
                                </div>
                            );
                        }

                        // The componentsPlacement is trying to use an area that DOES NOT exist in the layout therefore
                        // return an empty element.
                        return (
                            <div key={area} className={clsx(classes.area)} />
                        );
                    })}
                </div>
            ) : (
                <CircularProgress />
            )}
        </>
    );
};


// Register this component with the RitzComponentsCollection so that it can be used with the Ritz UI customization system
RitzComponentsCollection.getInstance().registerComponent('EntityDetailsGenericLayoutContainer', EntityDetailsGenericLayoutContainer);
